import { useRef, type RefObject } from 'react';

export interface PanelSize {
  width: number;
  height: number;
}

interface PanelResizeHandleProps {
  label: string;
  panelRef: RefObject<HTMLElement>;
  widthRef?: RefObject<HTMLElement>;
  onResize: (size: PanelSize) => void;
  corner?: 'left' | 'right';
}

export function PanelResizeHandle({ label, panelRef, widthRef, onResize, corner = 'right' }: PanelResizeHandleProps) {
  const dragRef = useRef<(PanelSize & { x: number; y: number }) | null>(null);
  const direction = corner === 'left' ? -1 : 1;

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`absolute bottom-0 z-30 flex h-6 w-6 touch-none items-center justify-center bg-white text-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 dark:bg-gray-800 dark:text-gray-400 ${corner === 'left' ? 'left-0 cursor-nesw-resize rounded-tr' : 'right-0 cursor-nwse-resize rounded-tl'}`}
      onPointerDown={event => {
        const rect = panelRef.current?.getBoundingClientRect();
        if (!rect) return;
        const width = widthRef?.current?.getBoundingClientRect().width ?? rect.width;
        dragRef.current = { x: event.clientX, y: event.clientY, width, height: rect.height };
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
      }}
      onPointerMove={event => {
        const drag = dragRef.current;
        if (drag) onResize({ width: drag.width + direction * (event.clientX - drag.x), height: drag.height + event.clientY - drag.y });
      }}
      onPointerUp={event => {
        dragRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
      }}
      onPointerCancel={() => { dragRef.current = null; }}
      onLostPointerCapture={() => { dragRef.current = null; }}
      onKeyDown={event => {
        const dx = event.key === 'ArrowRight' ? 20 * direction : event.key === 'ArrowLeft' ? -20 * direction : 0;
        const dy = event.key === 'ArrowDown' ? 20 : event.key === 'ArrowUp' ? -20 : 0;
        if (dx || dy) {
          event.preventDefault();
          const rect = panelRef.current?.getBoundingClientRect();
          if (rect) {
            const width = widthRef?.current?.getBoundingClientRect().width ?? rect.width;
            onResize({ width: width + dx, height: rect.height + dy });
          }
        }
      }}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className={corner === 'left' ? '-scale-x-100' : undefined} aria-hidden="true">
        <path d="M4 13L13 4M8 13L13 8M12 13L13 12" />
      </svg>
    </button>
  );
}
